import Link from "next/link";
import Image from "@/components/Image";
import { STANDINGS_TEAMS_LINK_CLICKED } from "@/constants/amplitude";
import { CTP_GAMES, CTP_TEAM } from "@/types/divisionStandings";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import LossIcon from "../svg/LossIcon";
import WinIcon from "../svg/WinIcon";
import Styles from "./Standings.module.scss";

interface RecordTableProps {
  team: NonNullable<CTP_TEAM>;
}

type ResultT = NonNullable<CTP_GAMES>;

const ResultIcon = (record: ResultT | null) => {
  if (
    (!record?.teamPoints && !record?.opponentPoints) ||
    record?.teamPoints === record?.opponentPoints
  ) {
    return null;
  }
  if ((record?.teamPoints ?? 0) > (record?.opponentPoints ?? 0)) {
    return <WinIcon />;
  }

  if ((record?.teamPoints ?? 0) < (record?.opponentPoints ?? 0)) {
    return <LossIcon />;
  }
};

export default function RecordTable({ team }: RecordTableProps) {
  const { record } = team || {};
  const wins = record
    ? (record.nonConference?.wins ?? 0) + (record.conferenceRecord?.wins ?? 0)
    : 0;
  const losses = record
    ? (record.nonConference?.losses ?? 0) +
      (record.conferenceRecord?.losses ?? 0)
    : 0;
  const ties = record
    ? (record.nonConference?.ties ?? 0) + (record.conferenceRecord?.ties ?? 0)
    : 0;
  const winLoss = `${wins}-${losses}`;
  const teamRecord = ties > 0 ? `${winLoss}-${ties}` : winLoss;
  const nextGame = team.teamSchedule?.[1]?.games?.[0];
  const { logAmplitudeEvent } = useAmplitude();
  return (
    <div key={team.meta?.code} className={Styles.teamRecordRow}>
      <span className={Styles.teamRow}>
        <Link
          target="_blank"
          href={`https://sportsbook.fanduel.com/teams/nfl/${team.meta?.slug}/odds`}
          onClick={() => {
            logAmplitudeEvent(STANDINGS_TEAMS_LINK_CLICKED, {
              Module: "standings",
              "Link Text": team.meta?.code ?? "",
              "Link URL": `https://sportsbook.fanduel.com/teams/nfl/${team.meta?.slug}/odds`,
              "Page Name": "schedule",
              Competition: "NFL",
            });
          }}
        >
          <Image
            src={team.meta?.icon}
            alt={`${team.meta?.name} logo`}
            height="11"
            width="24"
          />
          <span className={Styles.teamName}>{team.meta?.code}</span>
        </Link>
        <span className={Styles.record}>{teamRecord}</span>
      </span>
      <div className={Styles.schedule}>
        {team.teamSchedule?.[0]?.games
          ?.filter((game) => game?.teamPoints ?? game?.opponentPoints)
          .slice(-5)
          .map((game) => {
            return (
              <div
                key={`${game?.opponentTeamMeta?.icon} ${game?.gameTitle}`}
                className={Styles.scheduleItem}
              >
                <Image
                  src={game?.opponentTeamMeta?.icon}
                  alt="Opponent logo"
                  height="11"
                  width="24"
                />
                <span className={Styles.overlayIcon}>{ResultIcon(game)}</span>
              </div>
            );
          })}
        {nextGame ? (
          <div
            key={`${nextGame?.opponentTeamMeta?.icon} + ${nextGame?.weekNumber}`}
            className={Styles.scheduleItem}
          >
            <Image
              src={nextGame?.opponentTeamMeta?.icon}
              alt="Opponent logo"
              height="11"
              width="24"
            />
          </div>
        ) : (
          <span className={Styles.nextGame}>-</span>
        )}
      </div>
    </div>
  );
}
